<!-- A group's members: who is in it, add your friends, rename (owner), leave.
     Opened from the group thread's Members button. Screens role. -->
<template>
  <IonModal
    :is-open="isOpen"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    class="pm-scr-sheet pm-scr-sheet--auto"
    @will-present="onPresent"
    @did-dismiss="onDismiss"
  >
    <div v-if="group" class="pm-scr-sheet-body">
      <div class="pm-scr-sheet-head">
        <h2 class="section-title">{{ group.name }} · {{ group.members.length }} pagers</h2>
        <IonButton fill="clear" type="button" @click="$emit('close')">Done</IonButton>
      </div>

      <!-- Rename: the owner's -->
      <form v-if="isOwner" class="pm-scr-field" novalidate @submit.prevent="rename.run()">
        <label class="pm-scr-label" :for="`${uid}-group-rename`">Group name</label>
        <div class="pm-scr-inline">
          <input
            :id="`${uid}-group-rename`"
            v-model="newName"
            class="pm-scr-input pm-scr-input--lcd pm-scr-upper"
            type="text"
            maxlength="16"
            autocomplete="off"
            autocapitalize="characters"
            @input="newName = newName.toUpperCase().replace(/[^A-Z0-9 .'&!_-]/g, '')"
          >
          <IonButton type="submit" fill="outline" :disabled="rename.busy.value || newName.trim() === group.name">Rename</IonButton>
        </div>
        <p v-if="rename.error.value" class="field-error" role="alert">{{ rename.error.value }}</p>
      </form>

      <section :aria-labelledby="`${uid}-members-h`">
        <h3 :id="`${uid}-members-h`" class="pm-scr-label">In the group</h3>
        <ul class="pm-scr-rows">
          <li v-for="m in group.members" :key="m.number" class="pm-scr-row">
            <div class="pm-scr-row-main pm-scr-row-static">
              <LcdChip :text="chipText(label(m.number))" />
              <span class="pm-scr-row-text">
                {{ label(m.number) }}
                <span v-if="m.number === group.owner" class="pm-scr-tag">OWNER</span>
                <span v-if="friendSecondary(m.number)" class="pm-scr-muted pm-scr-small pm-scr-block">{{ friendSecondary(m.number) }}</span>
              </span>
            </div>
            <template v-if="m.number !== me.number">
              <IonButton fill="clear" size="small" :aria-label="`Save ${label(m.number)} as`" @click="promptName(m.number)">Save as</IonButton>
              <IonButton
                v-if="isOwner"
                fill="clear"
                size="small"
                color="danger"
                :aria-label="`Remove ${label(m.number)} from ${group.name}`"
                :disabled="busyFor === m.number"
                @click="remove(m.number)"
              >Remove</IonButton>
            </template>
          </li>
        </ul>
        <p v-if="removeError" class="field-error" role="alert">{{ removeError }}</p>
      </section>

      <!-- Add: any member adds their own friends -->
      <section v-if="room > 0" class="pm-scr-stack">
        <FriendChecklist
          v-model="picked"
          :friends="choices"
          :room="room"
          legend="Add your friends"
          empty="Everyone on your friends list is already in it."
        />
        <IonButton v-if="choices.length" expand="block" fill="outline" :disabled="!picked.length || add.busy.value" @click="add.run()">
          {{ add.busy.value ? 'Adding…' : picked.length ? `Add ${picked.length}` : 'Add' }}
        </IonButton>
        <p v-if="add.error.value" class="field-error" role="alert">{{ add.error.value }}</p>
      </section>
      <p v-else class="pm-scr-muted pm-scr-small">This group is full — {{ MAX_GROUP }} pagers.</p>

      <IonButton expand="block" fill="clear" color="danger" :disabled="leave.busy.value" @click="confirmLeave">Leave group</IonButton>
      <p v-if="leave.error.value" class="field-error" role="alert">{{ leave.error.value }}</p>
    </div>
  </IonModal>
</template>

<script setup>
import { userMessage } from './user-message.js';
import { ref, computed, useId } from 'vue';
import { IonModal, IonButton, alertController } from '@ionic/vue';
import { useAction, toast } from '../core/ui/index.js';
import {
  me, groupById, friendName, friendSecondary, addableFriends, addToGroup, leaveGroup, removeFromGroup,
  renameGroup, MAX_GROUP,
} from '../store.js';
import { chipText } from '../pm/format.js';
import { promptName } from './name-prompt.js';
import FriendChecklist from './FriendChecklist.vue';
import LcdChip from './LcdChip.vue';

const uid = `pm${useId()}`;

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  groupId: { type: String, required: true },
});
const emit = defineEmits(['close', 'left']);

const group = computed(() => groupById(props.groupId));
const isOwner = computed(() => group.value?.owner === me.value.number);
const room = computed(() => MAX_GROUP - (group.value?.members.length ?? MAX_GROUP));
const choices = computed(() => addableFriends(props.groupId));
const label = (n) => (n === me.value.number ? `You${me.value.handle ? ` (${me.value.handle})` : ''}` : friendName(n));

const newName = ref('');
const picked = ref([]);
const busyFor = ref('');
const removeError = ref('');

const must = (r, fallback) => {
  if (r && r.ok === false) throw new Error(userMessage(r, fallback));
  return r;
};

const rename = useAction(async () => {
  must(await renameGroup(props.groupId, newName.value), "Couldn't rename the group. Try again.");
}, { done: 'Group renamed' });

const add = useAction(async () => {
  must(await addToGroup(props.groupId, picked.value), "Couldn't add them. Try again.");
  picked.value = [];
}, { weight: 'pop', done: 'Added' });

async function remove(n) {
  const who = friendName(n);
  const a = await alertController.create({
    header: `Remove ${who}?`,
    message: `They'll stop getting ${group.value.name}'s pages. They aren't told why.`,
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Remove', role: 'destructive' }],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role !== 'destructive') return;
  busyFor.value = n;
  removeError.value = '';
  try {
    must(await removeFromGroup(props.groupId, n), "Couldn't remove them. Try again.");
    toast(`${who} removed`);
  } catch (e) {
    removeError.value = e.message;
  } finally {
    busyFor.value = '';
  }
}

const leave = useAction(async (block) => {
  const name = group.value?.name;
  must(await leaveGroup(props.groupId, { block }), "Couldn't leave the group. Try again.");
  toast(`You left ${name}`);
  emit('left');
}, { weight: 'pop' });

async function confirmLeave() {
  const a = await alertController.create({
    header: `Leave ${group.value.name}?`,
    message: "You'll stop getting its pages. Its history stays on this phone. The group isn't told why.",
    buttons: [
      { text: 'Cancel', role: 'cancel' },
      { text: "Leave, and don't let them add me back", role: 'destructive', data: 'block' },
      { text: 'Leave', role: 'destructive', data: 'leave' },
    ],
  });
  await a.present();
  const { role, data } = await a.onDidDismiss();
  if (role !== 'destructive') return;
  leave.run(data === 'block');
}

let opener = null;
function onPresent() {
  opener = document.activeElement;
  newName.value = group.value?.name ?? '';
  picked.value = [];
  removeError.value = '';
}
function onDismiss() {
  emit('close');
  const el = opener;
  opener = null;
  try { el?.focus?.(); } catch { /* gone */ }
}
</script>
